import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import Toast from "../Toast/Toast";
import { formatDate } from "../../utils/formatDate";
import greenTick from "../../resources/icons/greenTick.svg";
import warningInfoIcon from "../../resources/icons/warning-info.svg";
import revokedIcon from "../../resources/icons/revoked-lottie-icon.svg";
import { AlertTriangle } from "lucide-react";
import { POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";

export default function BulkVerificationApprovalModal({
  isModalOpen,
  toggleModal,
  actionType,
  selectedTransactions,
  issuerComments,
  setIssuerComments,
  refreshTable,
  updateSelectedTransactions,
}) {
  const [revokedCount, setRevokedCount] = useState(0);
  const [modalSelectedTransactions, setModalSelectedTransactions] = useState(
    []
  );
  const [allSelected, setAllSelected] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  function closeModal() {
    if (updateSelectedTransactions) {
      updateSelectedTransactions(modalSelectedTransactions);
    }
    toggleModal(null);
    setIsProcessing(false);
  }

  useEffect(() => {
    if (isModalOpen) {
      setIssuerComments("");

      setModalSelectedTransactions([...selectedTransactions]);
      setAllSelected(true);
      setIsProcessing(false);

      const revoked = selectedTransactions.filter(
        (transaction) => transaction.vc_status === "Revoked"
      ).length;

      setRevokedCount(revoked);
    }
  }, [isModalOpen, setIssuerComments, selectedTransactions]);

  const handleCheckboxChange = (transaction, isChecked) => {
    setModalSelectedTransactions((prevSelected) => {
      if (isChecked) {
        return [...prevSelected, transaction];
      } else {
        return prevSelected.filter(
          (t) => t.transaction_id !== transaction.transaction_id
        );
      }
    });
  };

  const handleHeaderCheckboxChange = () => {
    if (allSelected) {
      setModalSelectedTransactions([]);
    } else {
      setModalSelectedTransactions([...selectedTransactions]);
    }
    setAllSelected(!allSelected);
  };

  const handleModalConfirmAction = async () => {
    if (modalSelectedTransactions.length > 0) {
      setIsProcessing(true);

      try {
        const body = {
          transaction_id: modalSelectedTransactions.map(
            (transaction) => transaction.transaction_id
          ),
          verification_approval_status: actionType,
          ...(issuerComments?.trim() && {
            issuer_comments: issuerComments.trim(),
          }),
        };

        const response = await POST(
          EntityConstants.GET_VERIFICATION_APPROVALS_LIST,
          body
        );

        if (response.ok) {
          Toast({
            type: "success",
            title: "Transaction Updated",
            message: `${
              modalSelectedTransactions.length
            } transaction(s) have been ${actionType.toLowerCase()} successfully!`,
          });

          if (refreshTable) {
            refreshTable();
          }

          if (updateSelectedTransactions) {
            updateSelectedTransactions([]);
          }

          closeModal();
        } else {
          const errorData = await response.json();
          Toast({
            type: "error",
            title: "Error",
            message:
              errorData.message ||
              "An error occurred while updating the transaction.",
          });
          setIsProcessing(false);
        }
      } catch (error) {
        console.error("Error during the POST request:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "An error occurred while updating the transaction status.",
        });
        setIsProcessing(false);
      }
    }
  };

  const handleClose = () => {
    if (updateSelectedTransactions) {
      updateSelectedTransactions(modalSelectedTransactions);
    }
    toggleModal(null);
    setIsProcessing(false);
  };

  return (
    <>
      <Transition appear show={isModalOpen ? true : false} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25 backdrop-blur-md" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-6xl h-auto transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative">
                  <button
                    type="button"
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={handleClose}
                    disabled={isProcessing}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <Dialog.Title
                    as="h3"
                    className="text-xl font-bold leading-6 text-gray-800 mb-4"
                  >
                    {actionType === "Approved"
                      ? "Confirm Approvals"
                      : "Confirm Rejections"}
                  </Dialog.Title>

                  {actionType === "Approved" && revokedCount > 0 && (
                    <div className="p-3 bg-yellow-50 border-yellow-200 rounded-lg my-3">
                      <div className="flex items-center">
                        <div className="mr-3 text-cfIssuedCredentialTemplateCardStatusUnderReviewBG">
                          <AlertTriangle className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-sm text-cfIssuedCredentialTemplateCardStatusUnderReviewBG font-medium ">
                            <span>
                              You are about to approve{" "}
                              <span className="font-bold text-gray-500">
                                {selectedTransactions.length} credentials
                              </span>
                              {""}, including{" "}
                              <span className="font-bold text-cfIssuedCredentialTemplateCardStatusFailedBG">
                                {revokedCount}
                              </span>{" "}
                              that have been revoked.
                            </span>
                            <br />
                            <span>Do you want to continue?</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Table for selected transactions */}
                  <div className="mt-4 max-h-96 overflow-auto">
                    <table className="w-full min-w-[60rem] mb-4 divide-y divide-gray-200">
                      <thead className="bg-[#E0EAFF] sticky top-0 text-gray-900 p-4 h-14">
                        <tr>
                          <th className="px-4 py-2 text-center text-sm font-bold w-12">
                            <input
                              type="checkbox"
                              checked={allSelected}
                              onChange={handleHeaderCheckboxChange}
                              className="cursor-pointer rounded-lg"
                              disabled={isProcessing}
                            />
                          </th>
                          <th className="px-4 py-2 text-center text-sm font-bold">
                            Verifier Name
                          </th>
                          <th className="px-4 py-2 text-center text-sm font-bold">
                            <div className="flex flex-col items-center">
                              <span>Verifier Organization</span>
                            </div>
                          </th>
                          <th className="px-4 py-2 text-center text-sm font-bold">
                            Created At
                          </th>
                          <th className="px-4 py-2 text-center text-sm font-bold">
                            Amount Paid
                          </th>
                          <th className="px-4 py-2 text-center text-sm font-bold">
                            CredIssuer Verified
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedTransactions.map((transaction) => (
                          <tr key={transaction.transaction_id}>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={modalSelectedTransactions.some(
                                  (t) =>
                                    t.transaction_id ===
                                    transaction.transaction_id
                                )}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    transaction,
                                    e.target.checked
                                  )
                                }
                                className="cursor-pointer rounded-lg"
                                disabled={isProcessing}
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              {transaction.verifier_name || "N/A"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {transaction.verifier_org_name || "N/A"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {transaction.created_at
                                ? formatDate(transaction.created_at)
                                : "N/A"}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {transaction.verification_cost &&
                              parseFloat(transaction.verification_cost) !== 0
                                ? `₹ ${(
                                    parseFloat(transaction.verification_cost) /
                                    100
                                  ).toFixed(2)}`
                                : "Free of charge"}
                            </td>
                            <td className="px-4 py-3 flex justify-center items-center">
                              <p className="font-regular text-black text-[14px]">
                                {transaction.vc_status === "Verified" ? (
                                  <img
                                    src={greenTick}
                                    alt="Verified"
                                    className="w-10 h-10"
                                  />
                                ) : transaction.vc_status === "Revoked" ? (
                                  <img
                                    src={revokedIcon}
                                    alt="Not Verified"
                                    className="w-11 h-11"
                                  />
                                ) : (
                                  <img
                                    src={warningInfoIcon}
                                    alt="Not Verified"
                                    className="w-11 h-11"
                                  />
                                )}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4">
                    <label
                      htmlFor="issuerComments"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {actionType === "Approved"
                        ? "Please add a comment to approve:"
                        : "Please provide a reason for rejection:"}
                    </label>
                    <textarea
                      id="issuerComments"
                      value={issuerComments}
                      onChange={(e) => setIssuerComments(e.target.value)}
                      className={`w-full px-2 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 ${
                        actionType === "Approved"
                          ? "focus:ring-blue-500"
                          : "focus:ring-red-500"
                      }`}
                      rows={3}
                      placeholder={
                        actionType === "Approved"
                          ? "Enter approval comment..."
                          : "Enter rejection reason..."
                      }
                      style={{ fontSize: "0.875rem" }}
                      required={actionType === "Rejected"}
                      disabled={isProcessing}
                    />
                  </div>

                  <div className="mt-4 flex justify-end gap-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex rounded-[8px] items-center justify-center gap-2 w-[10rem] h-[2.5rem] bg-white text-black font-medium text-sm border border-gray-800 hover:bg-gray-100 transition duration-200"
                      disabled={isProcessing}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleModalConfirmAction}
                      className={`flex rounded-[8px] items-center justify-center gap-2 w-[10rem] h-[2.5rem] font-medium text-sm transition duration-200 ${
                        !issuerComments.trim() ||
                        modalSelectedTransactions.length === 0 ||
                        isProcessing
                          ? actionType === "Approved"
                            ? "bg-blue-300 cursor-not-allowed opacity-70 text-white"
                            : "bg-red-300 cursor-not-allowed opacity-70 text-white"
                          : actionType === "Approved"
                          ? "bg-[#3538CD] hover:bg-cfPrimaryDark text-white"
                          : "bg-[#D94D40] hover:bg-[#D94D40] text-white"
                      }`}
                      disabled={
                        !issuerComments.trim() ||
                        modalSelectedTransactions.length === 0 ||
                        isProcessing
                      }
                    >
                      {isProcessing ? (
                        "Processing..."
                      ) : (
                        <>
                          {actionType === "Approved" ? "Approve" : "Reject"} (
                          {modalSelectedTransactions.length})
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
